import { useEffect, useRef, useState } from 'react'
import { useThree, type ThreeEvent } from '@react-three/fiber'
import { multiplicar, paraArray, somar, subtrair, type Vetor3 } from '../../dominio/geometria/vetor3'
import { decomporAngulosCervicais, type EixoColuna } from '../../dominio/geometria/anguloExtracao'
import {
  direcaoDaSeta,
  orientacaoDaDirecao,
  type OrientacaoSeta,
} from '../../dominio/didatico/setaCervical'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Vetor3D } from '../componentes/Vetor3D'
import { Rotulo3D } from '../componentes/Rotulo3D'

interface Props {
  eixoColuna: EixoColuna
  orientacao: OrientacaoSeta
  aoAlterar: (orientacao: OrientacaoSeta) => void
}

const COMPRIMENTO_SETA = 0.6

const COR_POR_FAIXA: Record<ReturnType<typeof decomporAngulosCervicais>['faixa'], string> = {
  alinhado: CORES_SEMANTICAS.cervicalAlinhado,
  atencao: CORES_SEMANTICAS.cervicalAtencao,
  excessivo: CORES_SEMANTICAS.cervicalExcessivo,
}

/**
 * Seta cervical interativa: arrastável em 3D, com a ponta presa a uma esfera
 * de raio fixo em torno da base da cervical (`topoColuna`). Só dois graus de
 * liberdade (a direção) — o comprimento é decoração, não faz parte do estado.
 *
 * O arraste usa uma esfera invisível como superfície de captura: em vez de
 * reimplementar raycasting manualmente, aproveita que o próprio evento de
 * ponteiro do react-three-fiber já entrega o ponto de interseção em
 * `event.point` — a mesma técnica de "drag plane" comum em R3F, só que numa
 * esfera em vez de num plano, porque o que varia aqui é uma DIREÇÃO.
 */
export function SetaCervical({ eixoColuna, orientacao, aoAlterar }: Props) {
  const [arrastando, setArrastando] = useState(false)
  const controls = useThree((s) => s.controls) as { enabled?: boolean } | null
  const arrastandoRef = useRef(arrastando)
  arrastandoRef.current = arrastando

  const direcao = direcaoDaSeta(eixoColuna, orientacao)
  const angulos = decomporAngulosCervicais(eixoColuna, direcao)
  const cor = COR_POR_FAIXA[angulos.faixa]
  const ponta = somar(eixoColuna.topoColuna, multiplicar(direcao, COMPRIMENTO_SETA))

  useEffect(() => {
    if (controls) controls.enabled = !arrastando
    return () => {
      if (controls) controls.enabled = true
    }
  }, [arrastando, controls])

  // solta o arraste mesmo se o ponteiro sair da esfera de captura antes do
  // pointerup — sem isso, um movimento rápido deixa a seta "grudada" no cursor
  useEffect(() => {
    if (!arrastando) return
    const soltar = () => setArrastando(false)
    window.addEventListener('pointerup', soltar)
    return () => window.removeEventListener('pointerup', soltar)
  }, [arrastando])

  const atualizarNoPonto = (pontoMundo: Vetor3) => {
    const direcaoArrastada = subtrair(pontoMundo, eixoColuna.topoColuna)
    aoAlterar(orientacaoDaDirecao(eixoColuna, direcaoArrastada))
  }

  return (
    <group>
      <Vetor3D origem={eixoColuna.topoColuna} destino={ponta} cor={cor} espessura={5} />

      {/* alça de arraste, na ponta da seta */}
      <mesh
        position={paraArray(ponta)}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation()
          setArrastando(true)
        }}
      >
        <sphereGeometry args={[0.045, 16, 14]} />
        <meshStandardMaterial color={cor} emissive={cor} emissiveIntensity={0.6} />
      </mesh>

      {/* esfera de captura do arraste: invisível, raio fixo em torno da base cervical */}
      <mesh
        position={paraArray(eixoColuna.topoColuna)}
        visible={false}
        onPointerMove={(e: ThreeEvent<PointerEvent>) => {
          if (!arrastandoRef.current) return
          e.stopPropagation()
          atualizarNoPonto({ x: e.point.x, y: e.point.y, z: e.point.z })
        }}
        onPointerUp={() => setArrastando(false)}
      >
        <sphereGeometry args={[COMPRIMENTO_SETA, 24, 18]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      <Rotulo3D
        posicao={somar(ponta, { x: 0, y: 0.1, z: 0 })}
        texto={`${angulos.desvioTotalGraus.toFixed(0)}°`}
        variante="eixo"
        fator={4}
        sempreVisivel
      />
      {angulos.faixa !== 'alinhado' && (
        <Rotulo3D
          posicao={somar(ponta, { x: 0, y: -0.14, z: 0 })}
          texto={angulos.faixa === 'atencao' ? 'atenção' : 'excessivo'}
          variante="risco"
          fator={4}
          sempreVisivel
        />
      )}
    </group>
  )
}
